/**
 * Sales Upload Flow - Integrated Single Page
 * ===========================================
 * Complete upload workflow for sales-assisted pre-onboarding
 *
 * Steps:
 * 1. Search & select restaurant (Google Places API)
 * 2. Upload menu PDF
 * 3. Processing status
 * 4. Redirect to session detail
 *
 * Reuses: Google Places autocomplete, PDF upload logic
 * Tracks: sales_rep_user_id for session attribution
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  Upload,
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  Loader2,
  MapPin,
  Phone,
  Star,
  FileText,
  AlertCircle,
  X,
  ChevronDown,
  Check
} from 'lucide-react';
import { debounce } from 'lodash';
import { useSalesAuth } from '../contexts/SalesAuthContext';
import { frictionlessApi } from '../services/api/frictionlessApi';
import { frictionlessTempApi } from '../services/api/frictionlessTempApi';

// Step indicator component
const StepIndicator: React.FC<{ currentStep: number; totalSteps: number }> = ({ currentStep, totalSteps }) => {
  return (
    <div className="flex items-center justify-center mb-8">
      {[...Array(totalSteps)].map((_, index) => {
        const stepNum = index + 1;
        const isActive = stepNum === currentStep;
        const isCompleted = stepNum < currentStep;

        return (
          <React.Fragment key={stepNum}>
            <div
              className={`
                w-10 h-10 rounded-full flex items-center justify-center font-medium transition-all
                ${isCompleted
                  ? 'bg-green-400/20 text-green-300 border-2 border-green-400/30'
                  : isActive
                  ? 'bg-[#FDB913] text-black border-2 border-[#FDB913]'
                  : 'bg-white/10 text-white/60 border-2 border-white/20'
                }
              `}
            >
              {isCompleted ? <CheckCircle className="w-5 h-5" /> : stepNum}
            </div>
            {index < totalSteps - 1 && (
              <div
                className={`
                  w-16 h-0.5 mx-2 transition-all
                  ${isCompleted ? 'bg-green-400/30' : 'bg-white/20'}
                `}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const SalesUpload: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useSalesAuth();
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // State
  const [currentStep, setCurrentStep] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState<any>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Editable restaurant details
  const [restaurantName, setRestaurantName] = useState('');
  const [restaurantAddress, setRestaurantAddress] = useState('');
  const [restaurantCity, setRestaurantCity] = useState('');
  const [restaurantState, setRestaurantState] = useState('');
  const [restaurantZipcode, setRestaurantZipcode] = useState('');
  const [restaurantPhone, setRestaurantPhone] = useState('');
  const [restaurantWebsite, setRestaurantWebsite] = useState('');
  const [productType, setProductType] = useState<'virtual_assistant' | 'virtual_host'>('virtual_assistant');
  const [showProductDropdown, setShowProductDropdown] = useState(false);

  // Close product dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showProductDropdown && !(event.target as Element).closest('[data-product-dropdown]')) {
        setShowProductDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showProductDropdown]);

  // Parse restaurant name and location from search query
  const parseSearchQuery = (searchQuery: string): { restaurant: string; location: string | null } => {
    if (searchQuery.includes(',')) {
      const parts = searchQuery.split(',').map(s => s.trim());
      if (parts.length >= 2) {
        return { restaurant: parts[0], location: parts.slice(1).join(', ') };
      }
    }
    const statePattern = /\s+([A-Z]{2})$/;
    const stateMatch = searchQuery.match(statePattern);
    if (stateMatch) {
      return { restaurant: searchQuery.replace(statePattern, '').trim(), location: stateMatch[0].trim() };
    }
    return { restaurant: searchQuery, location: null };
  };

  // Fetch Google Places suggestions
  const fetchSuggestions = useCallback(
    debounce(async (query: string) => {
      if (query.length < 2) {
        setSuggestions([]);
        return;
      }

      setIsLoadingSuggestions(true);
      try {
        const parsed = parseSearchQuery(query);
        const results = await frictionlessApi.getRestaurantAutocomplete(
          parsed.restaurant,
          parsed.location || undefined
        );
        setSuggestions(results);
      } catch (error) {
        console.error('Failed to fetch suggestions:', error);
        setSuggestions([]);
      } finally {
        setIsLoadingSuggestions(false);
      }
    }, 300),
    []
  );

  useEffect(() => {
    fetchSuggestions(searchQuery);
  }, [searchQuery, fetchSuggestions]);

  // Handle restaurant selection
  const handleSelectRestaurant = (restaurant: any) => {
    setSelectedRestaurant(restaurant);
    setSearchQuery(restaurant.name);
    setSuggestions([]);

    // Populate editable fields from selected restaurant (including city/state/zip)
    setRestaurantName(restaurant.name || '');
    setRestaurantAddress(restaurant.address || '');
    setRestaurantCity(restaurant.city || '');
    setRestaurantState(restaurant.state || '');
    setRestaurantZipcode(restaurant.zip || '');
    setRestaurantPhone(restaurant.phone || '');
    setRestaurantWebsite(restaurant.website || '');

    setCurrentStep(2);
  };

  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        setError('Please upload a PDF file');
        return;
      }
      if (file.size > 15 * 1024 * 1024) { // 15MB
        setError('File size must be under 15MB');
        return;
      }
      setUploadedFile(file);
      setError(null);
    }
  };

  // Handle drag and drop
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        setError('Please upload a PDF file');
        return;
      }
      if (file.size > 15 * 1024 * 1024) {
        setError('File size must be under 15MB');
        return;
      }
      setUploadedFile(file);
      setError(null);
    }
  };

  // Handle upload and processing
  const handleUpload = async () => {
    if (!uploadedFile || !selectedRestaurant || !user) return;

    setIsUploading(true);
    setError(null);

    try {
      // Step 1: Create session with sales rep tracking via direct API call
      const response = await fetch('/api/frictionless/create-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          restaurant_name: restaurantName,
          location: restaurantAddress,
          restaurant_city: restaurantCity,
          restaurant_state: restaurantState,
          restaurant_zipcode: restaurantZipcode,
          source: 'sales_portal',
          sales_rep_user_id: user.sales_user_id,
          sales_rep_email: user.email,
          restaurant_phone: restaurantPhone,
          restaurant_website: restaurantWebsite,
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create session');
      }

      const createSessionResponse = await response.json();
      const sessionId = createSessionResponse.session_id;
      setSessionId(sessionId);

      // Step 2: Upload PDF to the session
      const uploadResponse = await frictionlessTempApi.uploadFile(sessionId, uploadedFile);

      if (!uploadResponse.success) {
        throw new Error('Upload failed');
      }

      // Step 2.5: Trigger extraction using existing frictionless endpoint
      const extractResponse = await fetch('/api/frictionless-onboarding/extract-uploaded-menu', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          session_id: sessionId,
          file_info: uploadResponse.file_info,  // Required field from upload response
          extract_type: 'manual_upload',
          restaurant_info: {
            name: restaurantName,
            phone: restaurantPhone,
            address: restaurantAddress,
            city: restaurantCity,
            state: restaurantState,
            zip: restaurantZipcode
          },
          sales_rep_user_id: user.sales_user_id,
          sales_rep_email: user.email
        })
      });

      if (!extractResponse.ok) {
        const error = await extractResponse.json();
        throw new Error(error.detail || 'Failed to start menu extraction');
      }

      setCurrentStep(3);
      setIsUploading(false);
      setIsProcessing(true);

      // Step 3: Poll for extraction status
      const pollInterval = setInterval(async () => {
        try {
          const statusResponse = await frictionlessTempApi.getSessionData(sessionId);

          if (statusResponse.session_data?.extraction_status === 'completed') {
            clearInterval(pollInterval);

            // Extract zipcode from address (5-digit format)
            const zipcodeMatch = restaurantAddress.match(/\b\d{5}\b/);
            const zipcode = zipcodeMatch ? zipcodeMatch[0] : '00000';

            // Step 4: Prepare menu for demo (creates demo user + session)
            const prepareResponse = await fetch('/api/sales/prepare-menu-for-demo', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('sales_token')}`
              },
              body: JSON.stringify({
                session_id: sessionId,
                restaurant_name: restaurantName,
                restaurant_phone: restaurantPhone,
                restaurant_email: null,
                restaurant_address: restaurantAddress,
                restaurant_zipcode: zipcode,
                location_name: 'Main Location',
                sales_rep_user_id: user.sales_user_id,
                sales_rep_email: user.email,
                product_type: productType
              })
            });

            if (!prepareResponse.ok) {
              const error = await prepareResponse.json();
              throw new Error(error.detail || 'Failed to prepare menu for demo');
            }

            const prepareData = await prepareResponse.json();

            // Step 5: Set demo session cookie for dashboard access
            document.cookie = `auth_token=${prepareData.demo_session_id}; path=/; max-age=${365 * 24 * 60 * 60}; SameSite=Lax`;

            // Clear any stale demo data from previous sessions
            localStorage.removeItem('current_rest_id');
            localStorage.removeItem('restaurantData');
            localStorage.removeItem('user');

            // Mark as demo user to bypass JWT validation
            localStorage.setItem('isDemo', 'true');

            setIsProcessing(false);

            // Step 6: Redirect to restaurant menu review page (edit menu)
            // Uses processing_id (menu_id) and rest_id for proper routing
            window.location.href = `/restaurant-menu-review?processing_id=${prepareData.menu_id}&rest_id=${prepareData.rest_id}`;

          } else if (statusResponse.session_data?.extraction_status === 'failed') {
            clearInterval(pollInterval);
            setIsProcessing(false);
            setError('Menu extraction failed. Please try again.');
          }
        } catch (err: any) {
          clearInterval(pollInterval);
          console.error('Failed to prepare demo:', err);
          setError(err.message || 'Failed to prepare menu. Please try again.');
          setIsProcessing(false);
        }
      }, 3000); // Poll every 3 seconds

      // Cleanup interval after 5 minutes
      setTimeout(() => {
        clearInterval(pollInterval);
        if (isProcessing) {
          setError('Processing timeout. Please check the sessions list.');
          setIsProcessing(false);
        }
      }, 300000);

    } catch (err: any) {
      console.error('Upload failed:', err);
      setError(err.message || 'Upload failed. Please try again.');
      setIsUploading(false);
    }
  };

  // Reset form
  const handleReset = () => {
    setCurrentStep(1);
    setSearchQuery('');
    setSelectedRestaurant(null);
    setUploadedFile(null);
    setError(null);
    setRestaurantName('');
    setRestaurantAddress('');
    setRestaurantPhone('');
    setRestaurantWebsite('');
  };

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white">New Upload</h1>
        <p className="mt-2 text-white/60">
          Upload a restaurant menu for pre-onboarding demo
        </p>
      </div>

      {/* Step Indicator */}
      <StepIndicator currentStep={currentStep} totalSteps={3} />

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-400/20 border border-red-400/30 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-300 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-300 font-medium">Error</p>
            <p className="text-red-300/80 text-sm mt-1">{error}</p>
          </div>
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-5 h-5 text-red-300 hover:text-red-200" />
          </button>
        </div>
      )}

      {/* Content Card */}
      <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg overflow-hidden">
        <AnimatePresence mode="wait">
          {/* Step 1: Search Restaurant */}
          {currentStep === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="p-8"
            >
              <h2 className="text-xl font-semibold text-white mb-2">Search Restaurant</h2>
              <p className="text-white/60 text-sm mb-6">
                Find the restaurant using Google Places (name, address, phone auto-filled)
              </p>

              {/* Search Input */}
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/60" />
                <input
                  ref={inputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="e.g., J Alexander's, Nashville TN"
                  className="w-full pl-12 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#FDB913] focus:border-transparent"
                  autoFocus
                />
                {isLoadingSuggestions && (
                  <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/60 animate-spin" />
                )}
              </div>

              {/* Suggestions Dropdown */}
              {suggestions.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg overflow-hidden"
                >
                  {suggestions.map((restaurant, index) => (
                    <button
                      key={index}
                      onClick={() => handleSelectRestaurant(restaurant)}
                      className="w-full p-4 text-left hover:bg-white/10 transition-colors border-b border-white/10 last:border-b-0"
                    >
                      <div className="flex items-start gap-3">
                        <MapPin className="w-5 h-5 text-[#FDB913] flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-white">{restaurant.name}</p>
                          {restaurant.address && (
                            <p className="text-sm text-white/60 mt-1 line-clamp-1">{restaurant.address}</p>
                          )}
                          <div className="text-xs text-white/60 flex items-center gap-3 mt-1">
                            {restaurant.rating && (
                              <span className="flex items-center gap-1">
                                <Star className="w-3 h-3 fill-[#FDB913] text-[#FDB913]" />
                                {restaurant.rating}
                              </span>
                            )}
                            {restaurant.phone && (
                              <span>{restaurant.phone}</span>
                            )}
                            {(restaurant.type === 'restaurant' || (restaurant.types && restaurant.types.includes('restaurant'))) && (
                              <span className="text-[#FDB913]/60">Restaurant</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </motion.div>
              )}
            </motion.div>
          )}

          {/* Step 2: Upload PDF */}
          {currentStep === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="p-8"
            >
              <h2 className="text-xl font-semibold text-white mb-2">Upload Menu PDF</h2>
              <p className="text-white/60 text-sm mb-6">
                Verify restaurant details below (editable if needed)
              </p>

              {/* Editable Restaurant Details */}
              <div className="space-y-4 mb-6 p-4 bg-white/5 border border-white/10 rounded-lg">
                <div>
                  <label className="block text-sm text-white/60 mb-2">Restaurant Name *</label>
                  <input
                    type="text"
                    value={restaurantName}
                    onChange={(e) => setRestaurantName(e.target.value)}
                    placeholder="Restaurant name"
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#FDB913] focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm text-white/60 mb-2">Address *</label>
                  <input
                    type="text"
                    value={restaurantAddress}
                    onChange={(e) => setRestaurantAddress(e.target.value)}
                    placeholder="Street address"
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#FDB913] focus:border-transparent"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm text-white/60 mb-2">City *</label>
                    <input
                      type="text"
                      value={restaurantCity}
                      onChange={(e) => setRestaurantCity(e.target.value)}
                      placeholder="City"
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#FDB913] focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-white/60 mb-2">State *</label>
                    <input
                      type="text"
                      value={restaurantState}
                      onChange={(e) => setRestaurantState(e.target.value.toUpperCase())}
                      placeholder="State"
                      maxLength={2}
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#FDB913] focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-white/60 mb-2">ZIP *</label>
                    <input
                      type="text"
                      value={restaurantZipcode}
                      onChange={(e) => setRestaurantZipcode(e.target.value)}
                      placeholder="ZIP code"
                      maxLength={10}
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#FDB913] focus:border-transparent"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-white/60 mb-2">Phone</label>
                    <input
                      type="text"
                      value={restaurantPhone}
                      onChange={(e) => setRestaurantPhone(e.target.value)}
                      placeholder="Phone number"
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#FDB913] focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-white/60 mb-2">Website</label>
                    <input
                      type="text"
                      value={restaurantWebsite}
                      onChange={(e) => setRestaurantWebsite(e.target.value)}
                      placeholder="Website URL"
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#FDB913] focus:border-transparent"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-sm text-white/60 mb-2">Product Interest</label>
                  <div className="relative" data-product-dropdown>
                    <button
                      type="button"
                      onClick={() => setShowProductDropdown(!showProductDropdown)}
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-[#FDB913] focus:border-transparent hover:bg-white/15 transition-colors"
                    >
                      <div>
                        <span className="font-medium">
                          {productType === 'virtual_assistant' ? 'Virtual Assistant' : 'Virtual Host'}
                        </span>
                        <span className="text-white/60 ml-2 text-sm">
                          {productType === 'virtual_assistant'
                            ? '(Menu Management - $49-199/mo)'
                            : '(AI Phone Ordering - $250-500/mo)'}
                        </span>
                      </div>
                      <ChevronDown className={`w-5 h-5 text-white/60 transition-transform ${showProductDropdown ? 'rotate-180' : ''}`} />
                    </button>

                    <AnimatePresence>
                      {showProductDropdown && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ duration: 0.15 }}
                          className="absolute z-50 w-full mt-2 bg-gray-900 border border-white/20 rounded-lg shadow-xl overflow-hidden"
                        >
                          <button
                            type="button"
                            onClick={() => {
                              setProductType('virtual_assistant');
                              setShowProductDropdown(false);
                            }}
                            className={`w-full px-4 py-4 text-left flex items-start gap-3 hover:bg-white/10 transition-colors ${productType === 'virtual_assistant' ? 'bg-[#FDB913]/10' : ''}`}
                          >
                            <div className={`mt-1 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${productType === 'virtual_assistant' ? 'border-[#FDB913] bg-[#FDB913]' : 'border-white/40'}`}>
                              {productType === 'virtual_assistant' && <Check className="w-3 h-3 text-black" />}
                            </div>
                            <div>
                              <div className="font-medium text-white">Virtual Assistant</div>
                              <div className="text-sm text-white/60 mt-0.5">Menu Management - $49-199/mo</div>
                              <div className="text-xs text-white/40 mt-1">AI-powered menu optimization, recommendations & ordering</div>
                            </div>
                          </button>

                          <div className="border-t border-white/10" />

                          <button
                            type="button"
                            onClick={() => {
                              setProductType('virtual_host');
                              setShowProductDropdown(false);
                            }}
                            className={`w-full px-4 py-4 text-left flex items-start gap-3 hover:bg-white/10 transition-colors ${productType === 'virtual_host' ? 'bg-[#FDB913]/10' : ''}`}
                          >
                            <div className={`mt-1 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${productType === 'virtual_host' ? 'border-[#FDB913] bg-[#FDB913]' : 'border-white/40'}`}>
                              {productType === 'virtual_host' && <Check className="w-3 h-3 text-black" />}
                            </div>
                            <div>
                              <div className="font-medium text-white">Virtual Host</div>
                              <div className="text-sm text-white/60 mt-0.5">AI Phone Ordering - $250-500/mo</div>
                              <div className="text-xs text-white/40 mt-1">AI-powered phone answering, reservations & takeout orders</div>
                            </div>
                          </button>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              </div>

              {/* Upload Area */}
              <div
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-white/30 rounded-lg p-12 text-center cursor-pointer hover:border-[#FDB913] hover:bg-white/5 transition-all"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf"
                  onChange={handleFileChange}
                  className="hidden"
                />
                {uploadedFile ? (
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-16 h-16 bg-[#FDB913]/20 rounded-full flex items-center justify-center">
                      <FileText className="w-8 h-8 text-[#FDB913]" />
                    </div>
                    <div>
                      <p className="text-white font-medium">{uploadedFile.name}</p>
                      <p className="text-white/60 text-sm mt-1">
                        {(uploadedFile.size / (1024 * 1024)).toFixed(2)} MB
                      </p>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setUploadedFile(null);
                      }}
                      className="text-red-300 hover:text-red-200 text-sm underline"
                    >
                      Remove file
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center">
                      <Upload className="w-8 h-8 text-white/60" />
                    </div>
                    <div>
                      <p className="text-white font-medium">Drop PDF here or click to browse</p>
                      <p className="text-white/60 text-sm mt-1">Maximum file size: 15MB</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Navigation Buttons */}
              <div className="flex items-center gap-3 mt-6">
                <button
                  onClick={() => {
                    setCurrentStep(1);
                    setUploadedFile(null);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </button>
                <button
                  onClick={handleUpload}
                  disabled={!uploadedFile || isUploading || !restaurantName.trim() || !restaurantAddress.trim() || !restaurantCity.trim() || !restaurantState.trim() || !restaurantZipcode.trim()}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-[#FDB913] text-black rounded-lg hover:bg-[#FDB913]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isUploading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      Submit & Process
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          )}

          {/* Step 3: Processing */}
          {currentStep === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="p-8"
            >
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-[#FDB913]/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  {isProcessing ? (
                    <Loader2 className="w-8 h-8 text-[#FDB913] animate-spin" />
                  ) : (
                    <CheckCircle className="w-8 h-8 text-green-300" />
                  )}
                </div>
                <h2 className="text-xl font-semibold text-white mb-2">
                  {isProcessing ? 'Processing Menu...' : 'Processing Complete!'}
                </h2>
                <p className="text-white/60 text-sm">
                  {isProcessing
                    ? 'Extracting menu items using AI. This usually takes 1-2 minutes.'
                    : 'Your menu has been processed successfully.'
                  }
                </p>
                {!isProcessing && sessionId && (
                  <button
                    onClick={() => navigate(`/sales-horse/sessions?highlight=${sessionId}`)}
                    className="mt-6 px-6 py-3 bg-[#FDB913] text-black rounded-lg hover:bg-[#FDB913]/90 transition-colors inline-flex items-center gap-2"
                  >
                    View Session
                    <ArrowRight className="w-4 h-4" />
                  </button>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Start Over Button */}
      {currentStep > 1 && currentStep < 3 && (
        <div className="mt-4 text-center">
          <button
            onClick={handleReset}
            className="text-white/60 hover:text-white text-sm underline"
          >
            Start over
          </button>
        </div>
      )}
    </div>
  );
};

export default SalesUpload;
